import { Injectable } from "@angular/core";
import LogtoClient from "@logto/browser";
import { environment } from "../../environments/environment";

@Injectable({
  providedIn: "root",
})
export class LogtoService {
  private client: LogtoClient | null = null;

  private getClient(): LogtoClient {
    if (!this.client) {
      const config: any = {
        endpoint: environment.logto_endpoint,
        appId: environment.logto_app_id,
      };
      if (environment.logto_api_resource) {
        config.resources = [environment.logto_api_resource];
      }
      this.client = new LogtoClient(config);
    }
    return this.client;
  }

  async signIn(firstScreen?: "register"): Promise<void> {
    const redirectUri =
      environment.logto_redirect_uri ||
      `${environment.app_url}/admin-login`;
    // `any` avoids friction with the SDK's exact SignInOptions typing; firstScreen
    // 'register' sends the user straight to the Logto registration screen.
    const options: any = { redirectUri };
    if (firstScreen) {
      options.firstScreen = firstScreen;
    }
    await this.getClient().signIn(options);
  }

  async handleSignInCallback(): Promise<void> {
    await this.getClient().handleSignInCallback(window.location.href);
  }

  async getAccessToken(): Promise<string | undefined> {
    try {
      const resource = environment.logto_api_resource || undefined;
      return await this.getClient().getAccessToken(resource);
    } catch {
      return undefined;
    }
  }

  isCallbackUrl(): boolean {
    const params = new URLSearchParams(window.location.search);
    return params.has("code") && params.has("state");
  }

  /**
   * Ends the Logto session server-side and redirects the browser to Logto's
   * end-session endpoint, then back to the post-logout URI. Clears the SDK's
   * own stored tokens too. The redirect URI must be registered in Logto under
   * "Post sign-out redirect URIs" — we reuse the /admin-login URI for that.
   */
  async signOut(): Promise<void> {
    const postLogoutRedirectUri =
      environment.logto_redirect_uri ||
      `${environment.app_url}/admin-login`;
    await this.getClient().signOut(postLogoutRedirectUri);
  }
}
